import { useAuthStore } from './auth-store';

const API_BASE = '/api/v1';

interface ApiError {
  code: string;
  message: string;
  details?: Array<{ field?: string; rule?: string; message?: string }>;
  request_id?: string;
}

export class ApiException extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: ApiError['details'];
  public readonly requestId?: string;

  constructor(status: number, body: { error?: ApiError }) {
    const error = body.error ?? { code: 'unknown', message: 'Error inesperado.' };
    super(error.message);
    this.name = 'ApiException';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
    this.requestId = error.request_id;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return false;
      }
      const data = (await res.json()) as {
        data: { access_token: string; user: { id: number; email: string; name: string; role: string } };
      };
      useAuthStore
        .getState()
        .setSession(data.data.access_token, data.data.user as ReturnType<typeof useAuthStore.getState>['user']);
      return true;
    } catch {
      useAuthStore.getState().clear();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function apiFetch<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const { skipRefresh: _sr1, body: _b1, ...restOpts1 } = options;
  void _sr1;
  void _b1;
  const init: RequestInit = {
    ...restOpts1,
    headers,
    credentials: 'include',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  };

  let res = await fetch(`${API_BASE}${path}`, init);

  // Si 401 y tenemos refresh, intentar y reintentar la request original.
  if (res.status === 401 && !options.skipRefresh && token) {
    const ok = await attemptRefresh();
    if (ok) {
      const newToken = useAuthStore.getState().accessToken;
      if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    }
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? (JSON.parse(text) as { data?: T; error?: ApiError } | T) : undefined;

  if (!res.ok) {
    throw new ApiException(res.status, (body ?? {}) as { error?: ApiError });
  }

  // Unwrap si viene con shape { data, meta? }; si no, devolver el body crudo.
  if (body && typeof body === 'object' && 'data' in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export async function apiFetchPaginated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T[]; meta: { total: number; page: number; per_page: number; last_page: number } }> {
  const headers = new Headers(options.headers ?? {});
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const { skipRefresh: _sr, body: _b, ...restOpts } = options;
  void _sr;
  void _b;
  const init: RequestInit = { ...restOpts, headers, credentials: 'include' };

  let res = await fetch(`${API_BASE}${path}`, init);
  if (res.status === 401 && !options.skipRefresh && token) {
    const ok = await attemptRefresh();
    if (ok) {
      headers.set('Authorization', `Bearer ${useAuthStore.getState().accessToken}`);
      res = await fetch(`${API_BASE}${path}`, { ...init, headers });
    }
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiException(res.status, body);
  return body;
}

export async function login(email: string, password: string) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json();
  if (!res.ok) throw new ApiException(res.status, body);
  return body.data as {
    access_token: string;
    user: { id: number; email: string; name: string; role: string };
  };
}

export async function logout() {
  try {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
  } finally {
    useAuthStore.getState().clear();
  }
}

export async function tryRestoreSession(): Promise<boolean> {
  return attemptRefresh();
}
